/**
 * Scanner page — full-screen camera view with ArUco marker detection.
 * Displays camera stream, runs detection, and overlays box info (name + contents).
 */

import { useOnboarding } from '../hooks/useOnboarding';
import { OnboardingWizard } from '../components/OnboardingWizard';
import { useMarkerTracking } from '../hooks/useMarkerTracking';
import { CameraView } from '../camera/CameraView';
import { MarkerOverlay, BoxInfoOverlay } from '../ar';

export function ScannerPage() {
  const { shouldShow, complete } = useOnboarding();
  const { trackedMarkers, cameraStatus, cameraError, startCamera, isDetecting, fps } =
    useMarkerTracking({
      enableAruco: true,
      enableQr: true,
    });

  // Get video dimensions for overlay mapping
  const videoEl = document.querySelector('video');
  const videoWidth = videoEl?.videoWidth || 1280;
  const videoHeight = videoEl?.videoHeight || 720;
  const maxDetectionWidth = 640;
  const scale = Math.min(maxDetectionWidth / videoWidth, 1);
  const detectionWidth = Math.round(videoWidth * scale);
  const detectionHeight = Math.round(videoHeight * scale);

  return (
    <>
      {shouldShow && <OnboardingWizard onComplete={complete} />}
      <div className="flex flex-col items-center justify-center min-h-[calc(100dvh-10rem)]">
        <div
          id="scanner-viewport"
          data-testid="scanner-viewport"
          className="relative w-full max-w-md aspect-[3/4] landscape:aspect-video rounded-2xl overflow-hidden bg-surface"
        >
          {cameraStatus === 'idle' && (
            <div className="flex flex-col items-center justify-center h-full border-2 border-dashed border-border">
              <svg
                className="h-16 w-16 text-text-secondary mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                />
              </svg>
              <p className="text-text-secondary text-sm text-center px-6 mb-4">
                Point your camera at boxes with ArUco markers or QR codes
              </p>
              <button
                onClick={() => startCamera()}
                className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent/90"
              >
                Start Camera
              </button>
            </div>
          )}
          {cameraStatus === 'requesting' && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin h-12 w-12 border-4 border-accent border-t-transparent rounded-full mb-4" />
              <p className="text-text-secondary text-sm">Requesting camera access...</p>
            </div>
          )}
          {cameraStatus === 'error' && cameraError && (
            <div className="flex flex-col items-center justify-center h-full px-6">
              <p className="text-red-500 mb-2">Camera Error</p>
              <p className="text-text-secondary text-xs mb-4">{cameraError.message}</p>
              <button
                onClick={() => startCamera()}
                className="px-4 py-2 bg-accent text-white rounded"
              >
                Try Again
              </button>
            </div>
          )}
          {cameraStatus === 'active' && videoEl && (
            <>
              <CameraView videoRef={{ current: videoEl }} />
              {trackedMarkers.length > 0 && (
                <>
                  {/* Marker outlines */}
                  <MarkerOverlay
                    markers={trackedMarkers}
                    videoWidth={videoWidth}
                    videoHeight={videoHeight}
                    detectionWidth={detectionWidth}
                    detectionHeight={detectionHeight}
                  />
                  {/* Box info overlays */}
                  <BoxInfoOverlay
                    trackedMarkers={trackedMarkers}
                    videoWidth={videoWidth}
                    videoHeight={videoHeight}
                    detectionWidth={detectionWidth}
                    detectionHeight={detectionHeight}
                  />
                </>
              )}
              <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                {isDetecting ? '🟢' : '⚪️'} {fps} fps
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
